import { deleteCookie } from '@do/seashell';
import { useMutation } from '@do/seashell/graphql';
import { useNavigate } from '@do/seashell/navigation';
import { Button, Text as WalrusText, Tooltip, style, Box } from '@do/walrus';
import { Form, Formik } from 'formik';
import _get from 'lodash/get';
import React, { ReactElement } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { PreferredLoginProps } from '../../src/interfaces/login';
import {
  DynamicVariantProps,
  VariantProps
} from '../../src/interfaces/variant';
import { teamVariant } from '../../src/login-variants';
import vars from '../assets/overwrite-vars.scss';
import successIcon from '../assets/success-icon.svg';
import { createClient } from '../client';
import {
  FORCE_EXTERNAL,
  LOGIN_ENDPOINT,
  LOGIN_REDIRECT_URL
} from '../constants';
import copy from '../copy';
import {
  loginAccount as LoginAccount,
  loginAccountVariables
} from '../generated-types/loginAccount';
import { LoginType } from '../interfaces/login';
import { LOG_IN_ACCOUNT } from '../queries/queries';
import {
  addSessionData,
  handleFormError,
  saveSessionData
} from '../utils/gql-utils';
import { safeGetUri } from '../utils/safe-get-uri';
import { replaceInterpolatedVariables } from '../utils/string-utils';
import ErrorBanner from './ErrorBanner';
import GithubButton from './GithubButton';
import GoogleButton from './GoogleButton';
import Link from './Link';
import SpinnerButton from './SpinnerButton';
import SuccessBanner from './SuccessBanner';
import Text from './Text';
import TextInput from './TextInput';
import Title from './Title';
import Wrapper from './Wrapper';

// Set AvatarProps to any because the native src attribute doesn't expect a possible null value
interface AvatarProps {
  src: string;
}

const NewFormHr = styled.hr`
  height: ${style.vars.borderWidth.thin};
  margin-bottom: 0;
  margin-top: ${style.vars.whitespace.medium};
  color: ${vars.grey7};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  border-radius: ${style.vars.borderRadius.base};
  border: ${style.vars.borderWidth.thin} solid ${style.colors.grey.primary};
  margin-bottom: ${style.vars.space['3']};
  padding: ${style.vars.space['3']};
`;

const NewUserInfo = styled(UserInfo)`
  margin: ${style.vars.space['4']} 0;
  padding: ${style.vars.space['4']};
  border-radius: ${vars.border_radius_xlarge};
  border-color: ${vars.grey6};
`;

const Avatar = styled.img<AvatarProps>`
  width: ${style.vars.space['9']};
  height: ${style.vars.space['9']};
  border-radius: 50%;
  margin-right: ${style.vars.space['4']};
`;

const Email = styled(WalrusText)`
  word-break: break-word;
`;

const ForgotPassword = styled.div`
  margin: ${style.vars.space['3']} 0 ${style.vars.space['2']};
  text-align: center;

  &:not(:last-child) {
    margin-top: ${style.vars.space['4']};
  }
`;

const ForgetPreferredSignInWrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: flex-end;
`;

const FormValidation = Yup.object().shape({
  email: Yup.string()
    .email(copy.form.validation.email_valid)
    .required(copy.form.validation.email_required),
  password: Yup.string().required(copy.form.validation.password_required)
});

const PreferredEmailValidation = Yup.object().shape({
  password: Yup.string().required(copy.form.validation.password_required)
});

interface Props {
  githubOauthUrl: string;
  googleOauthUrl: string;
  passwordSuccessBanner?: 'forgot' | 'reset';
  showNext: (page: LoginType) => void;
  user?: PreferredLoginProps;
  hasPreferredLogin: boolean;
  team?: {
    name: string;
  };
  variant: VariantProps | DynamicVariantProps;
}
const client = createClient(LOGIN_ENDPOINT);

const LoginForm: React.FC<Props> = ({
  githubOauthUrl,
  googleOauthUrl,
  passwordSuccessBanner,
  showNext,
  user,
  hasPreferredLogin,
  team,
  variant
}): ReactElement => {
  const [genericServerError, setGenericServerError] = React.useState();
  const [isRedirecting, setIsRedirecting] = React.useState(false);
  const [loginAccount, { loading: isLoading }] = useMutation<
    LoginAccount,
    loginAccountVariables
  >(LOG_IN_ACCOUNT, { client });

  const navigate = useNavigate();

  const userEmail = _get(user, 'email');
  const hasPreferredEmail = _get(user, 'sign_in_method') === 'email';
  const hasPreferredGoogle = _get(user, 'sign_in_method') === 'google';
  const hasPreferredGithub = _get(user, 'sign_in_method') === 'github';
  const joinTeamCopy = replaceInterpolatedVariables(team, teamVariant);

  const PREFERRED_SIGN_IN_COOKIE = '_digitalocean_remember_me';
  const TEAM_INVITE_COOKIE = 'invite_code';

  return (
    <Wrapper>
      <Formik
        validateOnBlur={false}
        initialValues={{
          email: team ? userEmail : '',
          password: ''
        }}
        validationSchema={
          hasPreferredEmail ? PreferredEmailValidation : FormValidation
        }
        onSubmit={(values, { setErrors }) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          setGenericServerError(null as any); /* Reset displayed error */

          loginAccount({
            variables: {
              loginRequest: addSessionData(values)
            }
          })
            .then((successResponse) => {
              const {
                status,
                session_data: sessionData,
                redirect_url: redirectURL,
                required_next_step: requiredNextStep
              } = (successResponse.data as LoginAccount).loginAccount ?? {};

              saveSessionData(sessionData as string);

              if (status === 'granted') {
                setIsRedirecting(true);
                window.location.assign(
                  safeGetUri(redirectURL as string, LOGIN_REDIRECT_URL)
                );
              } else if (status === 'pending') {
                showNext(requiredNextStep as LoginType);
              } else if (status === 'rejected') {
                setGenericServerError(copy.errors.incorrect_email_or_password);
              }
            })
            .catch(handleFormError(setErrors, setGenericServerError));
        }}
      >
        {({ errors, values, touched }) => (
          <>
            {passwordSuccessBanner && (
              <SuccessBanner>
                <img src={successIcon} alt="" />
                {copy.form.login.after_password[passwordSuccessBanner]}
              </SuccessBanner>
            )}

            <Title
              title={
                team
                  ? joinTeamCopy.title
                  : 'preferredLoginTitle' in variant
                  ? variant.title
                  : (variant as DynamicVariantProps).loginTitle
              }
            />

            {team ? (
              <Text>{joinTeamCopy.subtitle}</Text>
            ) : (
              !hasPreferredLogin &&
              'subtitle' in variant && <Text>{variant.subtitle}</Text>
            )}

            {hasPreferredLogin && (
              <>
                <Text>
                  {'preferredLoginSubtitle' in variant
                    ? variant.preferredLoginSubtitle
                    : (variant as DynamicVariantProps).loginSubtitle}
                </Text>
                <NewUserInfo>
                  <Avatar src={_get(user, 'avatar_url') || ''} alt="avatar" />
                  <Email>{userEmail}</Email>
                  <ForgetPreferredSignInWrapper>
                    <Tooltip content="forget this sign-in" placement="left">
                      {
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                        <span tabIndex={0}>
                          {/* The above span is necessary for the Tooltip component. See CENG-1691 for more. */}
                          <Button
                            variation="icon"
                            icon="close"
                            onClick={() => {
                              // Delete remember me cookie
                              deleteCookie(PREFERRED_SIGN_IN_COOKIE);

                              // Delete invite_code cookie when choosing to sign in as another user
                              deleteCookie(TEAM_INVITE_COOKIE);

                              // Reload /login with cookie deleted to not display preferred login
                              window.location.reload();
                            }}
                          />
                        </span>
                      }
                    </Tooltip>
                  </ForgetPreferredSignInWrapper>
                </NewUserInfo>
              </>
            )}

            {(!hasPreferredLogin || hasPreferredEmail) && (
              <Form autoComplete="none">
                {genericServerError && (
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  <ErrorBanner error={genericServerError as any} />
                )}

                {hasPreferredLogin ? (
                  <TextInput
                    placeholder={copy.form.login.password_placeholder}
                    required
                    name="password"
                    label={copy.form.password_label}
                    type="password"
                    touched={touched.password}
                    error={errors.password}
                    value={values.password}
                    autoComplete="none"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                  />
                ) : (
                  <>
                    <TextInput
                      placeholder={copy.form.email_placeholder}
                      required
                      name="email"
                      label={copy.form.login.email_label}
                      touched={touched.email}
                      error={errors.email}
                      value={values.email}
                      autoComplete="none"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    />
                    <TextInput
                      placeholder={copy.form.login.password_placeholder}
                      required
                      name="password"
                      label={copy.form.password_label}
                      type="password"
                      touched={touched.password}
                      error={errors.password}
                      value={values.password}
                      autoComplete="none"
                    />
                  </>
                )}
                <SpinnerButton
                  type="submit"
                  fullWidth={true}
                  disabled={isLoading || isRedirecting}
                  spinning={isLoading || isRedirecting}
                  loadingMessage={copy.loading.login.belowButton}
                >
                  {copy.form.login.button_text}
                </SpinnerButton>
              </Form>
            )}

            {!hasPreferredLogin && <NewFormHr />}

            {(!hasPreferredLogin || hasPreferredGoogle) && (
              <Box display="block" mt={style.vars.whitespace.small}>
                <GoogleButton
                  url={googleOauthUrl}
                  text={copy.form.oauth.login.google_button}
                  onClick={() => window.location.assign(googleOauthUrl)}
                />
              </Box>
            )}
            {(!hasPreferredLogin || hasPreferredGithub) && (
              <GithubButton
                url={githubOauthUrl}
                text={copy.form.oauth.login.github_button}
                onClick={() => window.location.assign(githubOauthUrl)}
              />
            )}

            {(!hasPreferredLogin || hasPreferredEmail) && (
              <ForgotPassword style={{ textAlign: 'left' }}>
                <Link
                  forceExternal={FORCE_EXTERNAL}
                  to="/forgot_password/new"
                  onClick={(e) => {
                    navigate('/forgot_password/new', {
                      state: { email: !hasPreferredLogin && values.email }
                    });
                    e.preventDefault();
                  }}
                >
                  {copy.form.forgot_password}
                </Link>
              </ForgotPassword>
            )}

            {hasPreferredLogin && (
              <Box pt={3} pb={1} display="block">
                <span className="need-help-text">{copy.form.not_you}</span>
                <br />
                <Link
                  to={window.location.pathname + window.location.search}
                  onClick={(e) => {
                    // Delete invite_code cookie when choosing to sign in as another user
                    deleteCookie(TEAM_INVITE_COOKIE);

                    navigate(
                      window.location.pathname + window.location.search,
                      {
                        state: { preferredLoginState: 'none' }
                      }
                    );

                    e.preventDefault();

                    // Reload /login with new state to not display preferred login
                    window.location.reload();
                  }}
                >
                  {copy.form.login_different_account}
                </Link>
              </Box>
            )}
          </>
        )}
      </Formik>
    </Wrapper>
  );
};

export default LoginForm;
